import { COMPANY, GRIEVANCE_OFFICER } from '../company';
import type { LegalDoc } from './types';

/**
 * Nurture's privacy policy, in the numbered format of zenquip.com's, written for the Nurture web
 * app. Needs a legal read before production (PROGRESS W3).
 */
export const PRIVACY: LegalDoc = {
  title: 'Privacy policy',
  summary:
    'We collect only what we need to prepare your child’s report, we never sell it or use it for advertising, and you can see, correct or delete it at any time.',
  updated: '28 September 2026',
  sections: [
    {
      id: 'about-us',
      heading: 'About us',
      blocks: [
        `Nurture is a product of **${COMPANY.legalName}** (“ZenQuip”, “we”, “us”), part of the ZenQuip family of mental-health services.`,
        [
          `Registered office: ${COMPANY.address}`,
          `CIN: ${COMPANY.cin}`,
          `Email: [${COMPANY.supportEmail}](mailto:${COMPANY.supportEmail}) · Grievances: [${GRIEVANCE_OFFICER.email}](mailto:${GRIEVANCE_OFFICER.email}) · Phone: ${GRIEVANCE_OFFICER.phone}`,
        ],
        'Under India’s Digital Personal Data Protection Act, 2023 (the “DPDP Act”) we are the Data Fiduciary for the personal data described here.',
      ],
    },
    {
      id: 'scope',
      heading: 'Scope',
      blocks: [
        'This policy covers the Nurture web app, its reports and home programmes, and the messages we send you about them. ZenQuip’s other services have their own policies on zenquip.com.',
        'Nurture is for parents and legal guardians. The account holder is always an adult; we do not offer accounts to children.',
      ],
    },
    {
      id: 'what-we-collect',
      heading: 'What we collect',
      blocks: [
        '**About you:** your name, mobile number and/or email address, country, language, and, if you set one, a password (stored only as a one-way hash). For invoices, the billing name, phone number and address you give at checkout and, for Indian invoices, your state.',
        '**About your child:** first name, last name (optional), date of birth, gender, and anything you choose to add under “About my child” (for example a diagnosis, therapies, school, languages, what they love and what upsets them).',
        '**What you tell us in a home assessment:** your answers, notes, strengths you pick, and, if you enter them, results from earlier professional assessments.',
        '**What you record at home:** home-programme activity logs, goals, “moments” you log, and reminder settings.',
        '**People you add:** names and contact details of your child’s care team, family members you invite, and a nominee (see “Your rights”). Add them only if they would expect it.',
        '**Payments:** order and invoice records and the references our payment provider gives us. We never see or store card, UPI or bank details.',
        '**Technical:** the device and browser details needed to keep you signed in and the service secure, and security logs.',
        '**How you found us:** if you arrive from an ad, a campaign link or an invite code, which one (source, campaign and code). It is saved with your account when you sign up.',
        '**Visits:** which pages are visited and for how long. To count each visitor once a day without cookies, we turn your IP address and browser type into a one-way code that changes every day; the IP address itself is not kept and the codes are deleted within 3 days. If you are signed in, the visit is counted with your account in the same way. Other analytics tools run only if you allow them (see “Cookies”).',
        'We do not ask for your child’s photo, surname, school name or Aadhaar, and we ask you not to type them into free-text boxes.',
      ],
    },
    {
      id: 'how-we-use',
      heading: 'How we use it',
      blocks: [
        [
          'to prepare, deliver and store the reports and home programmes you ask for;',
          'to send you sign-in codes, receipts, report deliveries and, if you switch them on, daily reminders;',
          'to issue invoices and meet our tax and legal obligations;',
          'to keep the service secure, prevent misuse and fix problems;',
          'to answer you when you contact us;',
          'to understand, in totals, which pages and campaigns help parents;',
          'to improve our questions and content using information that no longer identifies you or your child.',
        ],
        'Your report and plan are prepared by fixed rules and content written with clinicians, not by an AI model. We do not use your or your child’s data to train AI models.',
      ],
    },
    {
      id: 'consent',
      heading: 'Consent and your child’s data',
      blocks: [
        'We process your child’s data on the basis of the consent you give, as their parent or legal guardian, when you create your account. You can withdraw that consent at any time; withdrawing it is as easy as giving it, and it does not affect what was done before.',
        'We do not track or monitor children’s behaviour for advertising, we show no advertising in Nurture, and we do not process children’s data in any way likely to harm their well-being.',
        'Optional things, tips by email or WhatsApp, daily reminders, need their own yes, and you can turn them off in Settings. Your consent history is in Settings → Privacy.',
      ],
    },
    {
      id: 'sharing',
      heading: 'Who we share it with',
      blocks: [
        'Only with service providers who process data for us under contract, for the purposes above:',
        [
          'Amazon Web Services (hosting, storage and email), in the Mumbai region, India;',
          'Razorpay (payments, for Indian customers);',
          'Meta’s WhatsApp Business platform (sign-in codes, report delivery and reminders you ask for);',
          'Google (Google Analytics) and PostHog (product analytics, EU servers), on our public pages and only if you allow them; see “Cookies”.',
        ],
        'We share a report with someone else only when you do it: by downloading it, sending it on WhatsApp, or making a report link or family invite in the app. Report links stop working on the date you choose, and you can stop them earlier.',
        'We may disclose information when the law requires it, or to protect someone’s life or safety.',
      ],
    },
    {
      id: 'no-selling',
      heading: 'We never sell your data',
      blocks: ['We do not sell, rent or trade personal data, and we do not use it for advertising.'],
    },
    {
      id: 'security',
      heading: 'How we protect it',
      blocks: [
        'Data is encrypted in transit and at rest. Access inside ZenQuip is limited to the people who need it for their work. Report PDFs are private and are only reachable through links that expire within minutes; share links you create expire on the date you choose.',
        'If a breach ever affects your data, we will tell you and the Data Protection Board of India as the law requires.',
      ],
    },
    {
      id: 'retention',
      heading: 'How long we keep it',
      blocks: [
        'Reports, answers and home logs are kept for 24 months after your last activity, unless you delete them sooner. When you delete your children’s data or your account in Settings, it is removed from the app at once; any copies in our backups expire within 35 days.',
        'Invoices and payment records are kept for as long as Indian tax law requires, with personal fields kept to a minimum. Security logs are kept as the law requires.',
      ],
    },
    {
      id: 'your-rights',
      heading: 'Your rights',
      blocks: [
        'Under the DPDP Act you can:',
        [
          '**see** a summary of the data we hold and how we use it: Settings → Download my data;',
          '**correct** anything that is wrong: edit it in the app, or ask us;',
          '**erase** your children’s data or your whole account: Settings, in one step;',
          '**withdraw consent** at any time;',
          '**nominate** someone to use these rights for you if you die or cannot act: Settings → Privacy;',
          '**complain** to our grievance officer, and then, if you are not satisfied, to the Data Protection Board of India.',
        ],
        'Can’t sign in? Use [Request data removal](/data-request). We will confirm it is you, usually with a one-time code to your registered number, before we act.',
      ],
    },
    {
      id: 'grievance-officer',
      heading: 'Grievance officer',
      blocks: [
        'To use any of the rights above, or to raise a privacy concern, contact our Grievance Officer:',
        [
          `**${GRIEVANCE_OFFICER.name}**, ${COMPANY.legalName}`,
          COMPANY.address,
          `Email: [${GRIEVANCE_OFFICER.email}](mailto:${GRIEVANCE_OFFICER.email})`,
          `Phone: [${GRIEVANCE_OFFICER.phone}](${GRIEVANCE_OFFICER.phoneHref})`,
        ],
        `We respond within ${GRIEVANCE_OFFICER.resolveDays} days.`,
      ],
    },
    {
      id: 'cookies',
      heading: 'Cookies and storage on your device',
      blocks: [
        '**Needed for the app:** your sign-in, your language and your cookie choice. These are always on.',
        '**Analytics, only if you choose “Allow analytics”:** we may use Google Analytics (Google LLC) and PostHog (PostHog Inc., servers in the EU). They run on our public pages (home, FAQ, Learn, Trust, legal pages and sign-in), never on home assessments, reports, the home programme or anything about your child. They store an identifier on your device (cookies named “_ga” and browser storage named “ph_…”) and receive the page address, your browser and device type, approximate location and the site you came from, so we can count visits. Advertising features, Google signals and session recording are off. The data is kept no longer than 12 months (Google Analytics: 2 months) and is processed outside India.',
        'You can change your choice at any time with “Cookie settings” at the bottom of every page. Saying no changes nothing else in the app. We do not use advertising cookies.',
      ],
    },
    {
      id: 'where-stored',
      heading: 'Where your data is stored',
      blocks: [
        'In India (Amazon Web Services, Mumbai), for families in India and elsewhere. The one exception is analytics data if you allow it (see “Cookies”), which Google and PostHog process outside India. If anything else changes, we will update this policy first and follow the DPDP Act’s rules on transfers.',
      ],
    },
    {
      id: 'changes',
      heading: 'Changes to this policy',
      blocks: [
        'We will post changes here and change the date above. If a change affects how we use data you have already given us, we will tell you in the app or by message before it applies.',
      ],
    },
    {
      id: 'contact',
      heading: 'Contact',
      blocks: [
        `Questions about Nurture: [${COMPANY.supportEmail}](mailto:${COMPANY.supportEmail}). Privacy requests and complaints: [${GRIEVANCE_OFFICER.email}](mailto:${GRIEVANCE_OFFICER.email}).`,
      ],
    },
  ],
};
